import { type ReactNode } from 'react';

import { TypographyVariant } from '@ui/components/base/typography';
import { CustomIconButton } from '@ui/components/buttons/customIconButton';
import { OverflowTypography } from '@ui/components/fields/overflowTypography';
import { TrashIcon } from '@ui/components/icons';
import { Div } from '@ui/components/layout/Div';
import { cn } from '@ui/lib/utils';

enum NotificationCardVariant {
  DEFAULT = 'default',
  DESTRUCTIVE = 'destructive',
}

const TITLE_VARIANTS: Record<NotificationCardVariant, TypographyVariant> = {
  [NotificationCardVariant.DEFAULT]: TypographyVariant.STRONG,
  [NotificationCardVariant.DESTRUCTIVE]: TypographyVariant.DESTRUCTIVE,
};

interface NotificationCardProps {
  icon: ReactNode;
  title: string;
  variant?: NotificationCardVariant;
  /** Already formatted relative date: the locale stays in the app. */
  timestamp?: string;
  onDelete: () => void;
  children: ReactNode;
  className?: string;
}

const NotificationCard = ({
  icon,
  title,
  variant = NotificationCardVariant.DEFAULT,
  timestamp,
  onDelete,
  children,
  className,
}: NotificationCardProps) => (
  <Div
    column
    data-slot="notification-card"
    data-variant={variant}
    className={cn('group/notification relative w-full gap-1 rounded-lg bg-surface-sunken px-3.75 py-5', className)}
  >
    <Div row className="items-center justify-between gap-2">
      <Div row className="min-w-0 items-center gap-2">
        {/* Every icon draws its own disc and ring, so no fill is set here: an inherited fill floods the ring path */}
        <Div className="shrink-0 [&>svg]:size-6.75 [&>svg]:shrink-0">{icon}</Div>
        <OverflowTypography variant={TITLE_VARIANTS[variant]} size="base" text={title} bold className="min-w-0" />
      </Div>
      <CustomIconButton icon={TrashIcon} onClick={onDelete} className="shrink-0 text-fg" aria-label="delete" />
    </Div>
    {timestamp !== undefined && <Div className="pb-1.25 text-xs text-muted-foreground">{timestamp}</Div>}
    <Div column className="gap-1 text-xs text-fg">
      {children}
    </Div>
  </Div>
);

export { NotificationCard, NotificationCardVariant };
export type { NotificationCardProps };
